import React, { useState, useEffect } from 'react';
import { orderBy } from 'lodash';
import AppSelect from '@nxsys/components/AppSelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { TaxYearOption, TaxYearSelectProps } from './types';
import { getRangeYear } from '@nxsys/utils/taxYear';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';

const TaxYearSelect: React.FC<TaxYearSelectProps> = ({
  initValue,
  onFilter,
  className,
  placeholder,
  disabled = false,
  allowClear = false,
  noDefaultValue = false,
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;
  const taxYearRange = orderBy(getRangeYear(isIreland), 'value', 'desc');

  const [taxYear, setTaxYear] = useState<TaxYearOption | undefined>(
    noDefaultValue
      ? undefined
      : initValue !== undefined
        ? taxYearRange.find((opt) => opt.value === initValue)
        : taxYearRange[0],
  );

  useEffect(() => {
    if (initValue !== undefined) {
      setTaxYear(
        taxYearRange.find((opt) => opt.value === initValue) || taxYearRange[0],
      );
    }
  }, [initValue]);

  const handleFilter = (option: { label: string; value: string }) => {
    if (!option) {
      setTaxYear(undefined);
      onFilter({ taxYear: undefined });
      return;
    }
    const selectedValue = parseInt(option.value, 10);
    const selectedOption = taxYearRange.find(
      (opt) => opt.value === selectedValue,
    );

    if (selectedOption) {
      setTaxYear(selectedOption);
      onFilter({ taxYear: selectedOption.value });
    }
  };

  return (
    <div className={className}>
      <AppSelect
        placeholder={placeholder || messages['common.selectTaxYear']}
        value={
          taxYear
            ? { label: taxYear.label, value: taxYear.value.toString() }
            : undefined
        }
        onChange={handleFilter}
        disabled={disabled}
        allowClear={allowClear}
        options={taxYearRange.map((option) => ({
          label: option.label,
          value: option.value.toString(),
        }))}
        className="min-w-[184px] lg:!min-w-[184px] sm:!w-full md:!w-full"
      />
    </div>
  );
};

export default TaxYearSelect;
